import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';

import {
  resolveSettingsLoadErrorDetails,
  SettingsActions,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import { ButtonModule } from 'primeng/button';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ServerFieldErrors } from '@tourfold/libraries/shared-util';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  ResponseError,
  TenantApi,
  TenantBrandingUploadStatus,
  TenantBrandingUploadStatusStatusEnum,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  BillingApi,
  FrontendConfigurationApi,
  type FrontendConfigurationAuthenticated,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { AuthService } from '@dispo-web/app/services/auth.service';
import { FrontendConfigurationService } from '@tourfold/shared-kernels/frontend-configuration';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import QRCode from 'qrcode';

import { SelectModule } from 'primeng/select';
import { mapCatalogOptionsToSelectOptions, SelectOption } from './settings-general-options';

type BrandingAssetType = 'logo' | 'favicon';

interface CapabilityPill {
  key: string;
  label: string;
  labelKey?: string;
}

const ENTITLEMENT_FEATURE_KEYS = [
  'ai-assistant',
  'document-management',
  'brands',
  'custom-domains',
  'mcp-access',
  'webhooks',
  'audit-log',
  'sso-login',
] as const;

const ENTITLEMENT_FEATURE_ORDER = new Map<string, number>(
  ENTITLEMENT_FEATURE_KEYS.map((featureKey, index) => [featureKey, index])
);

const PLUGIN_LABEL_OVERRIDES: Record<string, string> = {
  bw: 'BW',
  'bw-auth': 'BW Auth',
  'bw-tours': 'BW Tours',
  'bw-web': 'BW Web',
  oeamtc: 'OEAMTC',
};

@Component({
  selector: 'tf-settings-page-general',
  imports: [
    SettingsActions,
    ButtonModule,
    ReactiveFormsModule,
    InputTextModule,
    TranslateModule,
    ClipboardModule,
    TooltipModule,
    SelectModule,
    DialogModule,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './settings-general.component.html',
  styleUrl: './settings-general.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralSettings implements OnInit {
  private static readonly ALLOWED_BRANDING_MIME_TYPES = ['image/png', 'image/jpeg'];
  private static readonly BRANDING_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
  private static readonly BRANDING_UPLOAD_POLL_INTERVAL_MS = 1000;
  private static readonly BRANDING_UPLOAD_MAX_POLL_ATTEMPTS = 20;

  readonly tenantNameControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(295),
  ]);
  readonly languageControl = new FormControl('', [Validators.required, Validators.maxLength(10)]);
  readonly timezoneControl = new FormControl('', [Validators.required, Validators.maxLength(50)]);
  readonly currentLogoUrl = signal<string | null>(null);
  readonly currentFaviconUrl = signal<string | null>(null);
  readonly pendingLogoFile = signal<File | null>(null);
  readonly pendingFaviconFile = signal<File | null>(null);
  readonly pendingLogoPreviewUrl = signal<string | null>(null);
  readonly pendingFaviconPreviewUrl = signal<string | null>(null);
  readonly logoMarkedForRemoval = signal(false);
  readonly faviconMarkedForRemoval = signal(false);
  readonly logoUploadStatusMessage = signal<string | null>(null);
  readonly faviconUploadStatusMessage = signal<string | null>(null);
  readonly logoUploadErrorMessage = signal<string | null>(null);
  readonly faviconUploadErrorMessage = signal<string | null>(null);
  readonly logoPreviewUrl = computed(() =>
    this.logoMarkedForRemoval() ? null : (this.pendingLogoPreviewUrl() ?? this.currentLogoUrl())
  );
  readonly faviconPreviewUrl = computed(() =>
    this.faviconMarkedForRemoval()
      ? null
      : (this.pendingFaviconPreviewUrl() ?? this.currentFaviconUrl())
  );
  readonly tenantId = signal<string | null>(null);
  readonly qrCodeDialogVisible = signal(false);
  readonly qrCodeDataUrl = signal<string | null>(null);
  readonly enabledFeatureFlagPills = signal<CapabilityPill[]>([]);
  readonly enabledPluginPills = signal<CapabilityPill[]>([]);
  readonly featureFlagsLoaded = signal(false);
  readonly pluginsLoaded = signal(false);
  readonly featureFlagsLoadFailed = signal(false);
  readonly pluginsLoadFailed = signal(false);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly saveError = signal<string | null>(null);

  // Group the three tenant controls so that `ServerFieldErrors` can observe value changes and
  // clear stale backend errors per field. The individual controls keep their `[formControl]`
  // bindings in the template.
  private readonly tenantForm = new FormGroup({
    tenantName: this.tenantNameControl,
    language: this.languageControl,
    timezone: this.timezoneControl,
  });

  // Server-side validation errors (populated from RFC 9457 ProblemDetail responses).
  // The wire field `name` maps to the form control `tenantName`.
  readonly tenantServerErrors = new ServerFieldErrors(this.tenantForm, {
    pointerToField: { name: 'tenantName' },
  });

  languageOptions: SelectOption[] = [];
  timezoneOptions: SelectOption[] = [];

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly tenantApi = inject(TenantApi);
  private readonly frontendConfigurationApi = inject(FrontendConfigurationApi);
  private readonly billingFrontendApi = inject(BillingApi);
  private readonly authService = inject(AuthService);
  private readonly frontendConfigService = inject(FrontendConfigurationService);
  private readonly translate = inject(TranslateService);
  private readonly resourceAccessService = inject(ResourceAccessService);

  readonly canUpdate = computed(() => this.resourceAccessService.canPerform('tenant', 'update'));

  constructor() {
    effect(() => {
      const disabled = this.isLoading() || this.isSaving() || !this.canUpdate();
      if (disabled) {
        this.tenantNameControl.disable();
        this.languageControl.disable();
        this.timezoneControl.disable();
      } else {
        this.tenantNameControl.enable();
        this.languageControl.enable();
        this.timezoneControl.enable();
      }
    });
  }

  ngOnInit(): void {
    this.loadSettings();
    this.loadTenantId();
    void this.loadCapabilityMetadata();
  }

  private loadTenantId(): void {
    const id = this.authService.getTenantId();
    this.tenantId.set(id);
  }

  private async loadCapabilityMetadata(): Promise<void> {
    await Promise.all([this.loadFeatureFlags(), this.loadPlugins()]);
  }

  private async loadFeatureFlags(): Promise<void> {
    this.featureFlagsLoaded.set(false);
    this.featureFlagsLoadFailed.set(false);
    try {
      const overview = await this.billingFrontendApi.getBillingOverview();
      this.enabledFeatureFlagPills.set(
        this.toFeatureFlagPills(overview.currentSubscription?.plan.entitlementFeatureKeys ?? [])
      );
    } catch {
      this.enabledFeatureFlagPills.set([]);
      this.featureFlagsLoadFailed.set(true);
    } finally {
      this.featureFlagsLoaded.set(true);
    }
  }

  private async loadPlugins(): Promise<void> {
    this.pluginsLoaded.set(false);
    this.pluginsLoadFailed.set(false);
    try {
      const config = await firstValueFrom(
        this.frontendConfigService.getAuthenticatedConfigurationOnce()
      );
      this.enabledPluginPills.set(this.toPluginPills(config));
    } catch {
      this.enabledPluginPills.set([]);
      this.pluginsLoadFailed.set(true);
    } finally {
      this.pluginsLoaded.set(true);
    }
  }

  private toFeatureFlagPills(featureKeys: string[]): CapabilityPill[] {
    return Array.from(new Set(featureKeys.filter(Boolean)))
      .sort((a, b) => this.compareEntitlementFeatureKeys(a, b))
      .map((key) => {
        const labelKey = this.featureKeyLabelKey(key);
        return {
          key,
          label: this.formatCapabilityKey(key),
          ...(labelKey ? { labelKey } : {}),
        };
      });
  }

  private toPluginPills(config: FrontendConfigurationAuthenticated): CapabilityPill[] {
    return Array.from(new Set(pluginIds(config.plugins)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({ key, label: this.pluginLabel(key) }));
  }

  private compareEntitlementFeatureKeys(a: string, b: string): number {
    const aOrder = ENTITLEMENT_FEATURE_ORDER.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = ENTITLEMENT_FEATURE_ORDER.get(b) ?? Number.MAX_SAFE_INTEGER;
    return aOrder === bOrder ? a.localeCompare(b) : aOrder - bOrder;
  }

  private featureKeyLabelKey(featureKey: string): string | undefined {
    if (!ENTITLEMENT_FEATURE_ORDER.has(featureKey)) {
      return undefined;
    }

    return this.featureKeyTranslationKey(featureKey);
  }

  private featureKeyTranslationKey(featureKey: string): string {
    const translationKey = `SETTINGS_BILLING.FEATURES.${featureKey
      .replaceAll(/[^a-zA-Z0-9]+/g, '_')
      .toUpperCase()}`;
    return translationKey;
  }

  private pluginLabel(pluginKey: string): string {
    return PLUGIN_LABEL_OVERRIDES[pluginKey.toLowerCase()] ?? this.formatCapabilityKey(pluginKey);
  }

  private formatCapabilityKey(key: string): string {
    return key
      .split(/[-_]+/)
      .filter(Boolean)
      .map((part) =>
        part.length <= 3 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1)
      )
      .join(' ');
  }

  private async loadSettings(): Promise<void> {
    this.isLoading.set(true);
    this.saveError.set(null);
    try {
      await Promise.all([this.loadLanguageOptions(), this.loadTimezoneOptions()]);
      const tenant = await this.tenantApi.getTenant();
      this.tenantNameControl.setValue(tenant.name);
      this.ensureLanguageOption(tenant.language);
      this.ensureTimezoneOption(tenant.timezone);
      this.languageControl.setValue(tenant.language);
      this.timezoneControl.setValue(tenant.timezone);
      await this.loadBrandingUrls();
      this.resetPendingBrandingChanges();
      this.loadError.set(null);
      this.loadErrorDetails.set(null);
    } catch (error: unknown) {
      this.loadError.set(this.translate.instant('SETTINGS_GENERAL.ERROR.LOAD_TITLE'));
      this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
    } finally {
      this.isLoading.set(false);
    }
  }

  retryLoad(): void {
    void this.loadSettings();
  }

  onLogoSelected(event: Event): void {
    this.handleBrandingSelection(event, 'logo');
  }

  onFaviconSelected(event: Event): void {
    this.handleBrandingSelection(event, 'favicon');
  }

  removeLogo(): void {
    this.markBrandingForRemoval('logo');
  }

  undoRemoveLogo(): void {
    this.undoBrandingRemoval('logo');
  }

  clearPendingLogoSelection(): void {
    this.clearPendingBrandingSelection('logo');
    this.setBrandingStatusMessage('logo', null);
  }

  removeFavicon(): void {
    this.markBrandingForRemoval('favicon');
  }

  undoRemoveFavicon(): void {
    this.undoBrandingRemoval('favicon');
  }

  clearPendingFaviconSelection(): void {
    this.clearPendingBrandingSelection('favicon');
    this.setBrandingStatusMessage('favicon', null);
  }

  private handleBrandingSelection(event: Event, assetType: BrandingAssetType): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      input.value = '';
      return;
    }

    const validationError = this.validateBrandingFile(file);
    if (validationError) {
      input.value = '';
      this.setBrandingUploadErrorMessage(assetType, validationError);
      return;
    }

    // Clear previous selection and store the new file reference before resetting the input,
    // so the File object remains alive in all browsers (especially Safari).
    this.clearPendingBrandingSelection(assetType);
    if (assetType === 'logo') {
      this.pendingLogoFile.set(file);
      this.logoMarkedForRemoval.set(false);
    } else {
      this.pendingFaviconFile.set(file);
      this.faviconMarkedForRemoval.set(false);
    }
    this.setBrandingStatusMessage(
      assetType,
      this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.PENDING_UPLOAD_ON_SAVE')
    );

    // Load the preview before resetting the input. Safari invalidates the underlying file data
    // when input.value is cleared, even if a File reference is held. Resetting inside the
    // FileReader callbacks ensures the read completes before the input is reset.
    this.loadBrandingPreview(file, assetType, input);
  }

  private loadBrandingPreview(
    file: File,
    assetType: BrandingAssetType,
    input: HTMLInputElement
  ): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      // Reset input only after reading completes — resetting earlier invalidates file data in Safari.
      input.value = '';
      const dataUrl = e.target?.result as string;
      if (assetType === 'logo') {
        this.pendingLogoPreviewUrl.set(dataUrl);
      } else {
        this.pendingFaviconPreviewUrl.set(dataUrl);
      }
      // FileReader callbacks run outside Angular's zone in some browsers, so signal writes
      // alone may not schedule a CD cycle for OnPush components. markForCheck() ensures the
      // view is checked on the next cycle — harmless once the app switches to zoneless.
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  cancel(): void {
    this.tenantServerErrors.clear();
    this.saveError.set(null);
    this.loadSettings();
  }

  async save(): Promise<void> {
    if (
      this.tenantNameControl.invalid ||
      this.languageControl.invalid ||
      this.timezoneControl.invalid ||
      this.isSaving()
    ) {
      return;
    }

    const tenantName = this.tenantNameControl.value?.trim() ?? '';
    const language = this.languageControl.value?.trim() ?? '';
    const timezone = this.timezoneControl.value?.trim() ?? '';

    this.isSaving.set(true);
    this.saveError.set(null);
    this.setBrandingUploadErrorMessage('logo', null);
    this.setBrandingUploadErrorMessage('favicon', null);
    this.tenantServerErrors.clear();
    try {
      const tenant = await this.tenantApi.patchTenant({
        patchTenant: {
          name: tenantName,
          language,
          timezone,
        },
      });

      const logoFailed = !(await this.tryApplyPendingBrandingChanges('logo'));
      const faviconFailed = !(await this.tryApplyPendingBrandingChanges('favicon'));
      await this.loadBrandingUrls();
      if (!logoFailed) this.clearBrandingStateAfterSuccess('logo');
      if (!faviconFailed) this.clearBrandingStateAfterSuccess('favicon');

      this.tenantNameControl.setValue(tenant.name);
      this.ensureLanguageOption(tenant.language);
      this.ensureTimezoneOption(tenant.timezone);
      this.languageControl.setValue(tenant.language);
      this.timezoneControl.setValue(tenant.timezone);

      if (logoFailed || faviconFailed) {
        this.saveError.set(this.translate.instant('SETTINGS_GENERAL.ERROR.BRANDING_PARTIAL_FAILURE'));
      }
    } catch (error: unknown) {
      this.restorePendingBrandingStatusMessages();
      const applied = await this.tenantServerErrors.apply(error);
      if (applied && applied.fieldCount > 0) {
        if (applied.generalMessages.length > 0) {
          this.saveError.set(applied.generalMessages.join(' '));
        }
      } else {
        const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
        const fallback =
          joinedGeneral !== ''
            ? joinedGeneral
            : this.translate.instant('SETTINGS_GENERAL.ERROR.SAVE_MESSAGE');
        this.saveError.set(fallback);
      }
    } finally {
      this.isSaving.set(false);
    }
  }

  private async tryApplyPendingBrandingChanges(assetType: BrandingAssetType): Promise<boolean> {
    try {
      await this.applyPendingBrandingChanges(assetType);
      return true;
    } catch (error: unknown) {
      const detail =
        (await this.getErrorDetail(error)) ??
        this.translate.instant('SETTINGS_GENERAL.ERROR.BRANDING_UPLOAD_FAILED');
      this.setBrandingUploadErrorMessage(assetType, detail);
      this.setBrandingStatusMessage(assetType, this.resolvePendingBrandingStatusMessage(assetType));
      return false;
    }
  }

  private clearBrandingStateAfterSuccess(assetType: BrandingAssetType): void {
    this.clearPendingBrandingSelection(assetType);
    if (assetType === 'logo') {
      this.logoMarkedForRemoval.set(false);
    } else {
      this.faviconMarkedForRemoval.set(false);
    }
    this.setBrandingStatusMessage(assetType, null);
    this.setBrandingUploadErrorMessage(assetType, null);
  }

  async showTenantIdQrCode(): Promise<void> {
    const id = this.tenantId();
    if (!id) {
      return;
    }

    try {
      const dataUrl = await QRCode.toDataURL(id, { width: 256, margin: 2 });
      this.qrCodeDataUrl.set(dataUrl);
      this.qrCodeDialogVisible.set(true);
    } catch {
      this.saveError.set(this.translate.instant('SETTINGS_GENERAL.ERROR.QR_CODE_GENERATION_FAILED'));
    }
  }

  clearSaveError(): void {
    this.saveError.set(null);
  }

  private async loadBrandingUrls(): Promise<void> {
    const [logoResult, faviconResult] = await Promise.allSettled([
      this.resolveBrandingAssetUrl('logo'),
      this.resolveBrandingAssetUrl('favicon'),
    ]);

    this.currentLogoUrl.set(logoResult.status === 'fulfilled' ? logoResult.value : null);
    this.currentFaviconUrl.set(faviconResult.status === 'fulfilled' ? faviconResult.value : null);
  }

  private async applyPendingBrandingChanges(assetType: BrandingAssetType): Promise<void> {
    const file = this.getPendingBrandingFile(assetType);
    if (file) {
      await this.uploadBrandingAsset(assetType, file);
      return;
    }

    if (!this.isBrandingMarkedForRemoval(assetType) || !this.getBrandingUrl(assetType)) {
      return;
    }

    this.setBrandingStatusMessage(
      assetType,
      this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.REMOVING')
    );

    if (assetType === 'logo') {
      await this.tenantApi.deleteTenantLogo();
    } else {
      await this.tenantApi.deleteTenantFavicon();
    }

    this.setBrandingUrl(assetType, null);
    this.setBrandingStatusMessage(assetType, null);
  }

  private async uploadBrandingAsset(assetType: BrandingAssetType, file: File): Promise<void> {
    this.setBrandingStatusMessage(
      assetType,
      this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.REQUESTING_UPLOAD_URL')
    );
    const uploadIntent =
      assetType === 'logo'
        ? await this.tenantApi.createTenantLogoUploadIntent({
            createTenantBrandingUploadIntent: {
              originalFileName: file.name,
              mimeType: file.type,
            },
          })
        : await this.tenantApi.createTenantFaviconUploadIntent({
            createTenantBrandingUploadIntent: {
              originalFileName: file.name,
              mimeType: file.type,
            },
          });

    this.setBrandingStatusMessage(
      assetType,
      this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.UPLOADING')
    );
    await this.uploadFileToPresignedPost(uploadIntent.uploadUrl, uploadIntent.uploadFields, file);

    this.setBrandingStatusMessage(
      assetType,
      this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.PROCESSING')
    );
    const status = await this.pollUploadStatusUntilApplied(assetType, uploadIntent.fileId);
    const resolvedUrl = status.url ?? (await this.resolveBrandingAssetUrl(assetType));
    this.setBrandingUrl(assetType, resolvedUrl);
    this.setBrandingStatusMessage(assetType, null);
  }

  private async pollUploadStatusUntilApplied(
    assetType: BrandingAssetType,
    fileId: string
  ): Promise<TenantBrandingUploadStatus> {
    for (let attempt = 0; attempt < GeneralSettings.BRANDING_UPLOAD_MAX_POLL_ATTEMPTS; attempt++) {
      const status =
        assetType === 'logo'
          ? await this.tenantApi.getTenantLogoUploadStatus({ fileId })
          : await this.tenantApi.getTenantFaviconUploadStatus({ fileId });

      if (
        status.status === TenantBrandingUploadStatusStatusEnum.Failed ||
        status.status === TenantBrandingUploadStatusStatusEnum.UploadRejected
      ) {
        throw new Error(this.translate.instant('SETTINGS_GENERAL.ERROR.BRANDING_UPLOAD_REJECTED'));
      }

      if (status.applied) {
        return status;
      }

      await this.delay(GeneralSettings.BRANDING_UPLOAD_POLL_INTERVAL_MS);
    }

    throw new Error(this.translate.instant('SETTINGS_GENERAL.ERROR.BRANDING_UPLOAD_TIMEOUT'));
  }

  private async uploadFileToPresignedPost(
    uploadUrl: string,
    uploadFields: Record<string, string>,
    file: File
  ): Promise<void> {
    const formData = new FormData();
    Object.entries(uploadFields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(this.translate.instant('SETTINGS_GENERAL.ERROR.BRANDING_UPLOAD_TIMEOUT'));
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async resolveBrandingAssetUrl(assetType: BrandingAssetType): Promise<string | null> {
    try {
      const asset =
        assetType === 'logo'
          ? await this.tenantApi.getTenantLogo()
          : await this.tenantApi.getTenantFavicon();
      return asset.url ?? null;
    } catch (error: unknown) {
      if (this.isNotFoundResponse(error)) {
        return null;
      }
      throw error;
    }
  }

  private validateBrandingFile(file: File): string | null {
    const mimeType = file.type.toLowerCase();
    if (!GeneralSettings.ALLOWED_BRANDING_MIME_TYPES.includes(mimeType)) {
      return this.translate.instant('SETTINGS_GENERAL.ERROR.BRANDING_INVALID_FILE_TYPE');
    }

    if (file.size > GeneralSettings.BRANDING_MAX_FILE_SIZE_BYTES) {
      return this.translate.instant('SETTINGS_GENERAL.ERROR.BRANDING_FILE_TOO_LARGE');
    }

    return null;
  }

  private getBrandingUrl(assetType: BrandingAssetType): string | null {
    return assetType === 'logo' ? this.currentLogoUrl() : this.currentFaviconUrl();
  }

  private setBrandingUrl(assetType: BrandingAssetType, url: string | null): void {
    if (assetType === 'logo') {
      this.currentLogoUrl.set(url);
    } else {
      this.currentFaviconUrl.set(url);
    }
  }

  private clearPendingBrandingSelection(assetType: BrandingAssetType): void {
    if (assetType === 'logo') {
      this.pendingLogoFile.set(null);
      this.pendingLogoPreviewUrl.set(null);
    } else {
      this.pendingFaviconFile.set(null);
      this.pendingFaviconPreviewUrl.set(null);
    }
    this.setBrandingUploadErrorMessage(assetType, null);
  }

  private markBrandingForRemoval(assetType: BrandingAssetType): void {
    this.clearPendingBrandingSelection(assetType);
    this.setBrandingStatusMessage(
      assetType,
      this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.PENDING_REMOVE_ON_SAVE')
    );
    if (assetType === 'logo') {
      this.logoMarkedForRemoval.set(true);
    } else {
      this.faviconMarkedForRemoval.set(true);
    }
  }

  private undoBrandingRemoval(assetType: BrandingAssetType): void {
    if (assetType === 'logo') {
      this.logoMarkedForRemoval.set(false);
    } else {
      this.faviconMarkedForRemoval.set(false);
    }
    this.setBrandingStatusMessage(assetType, null);
    this.setBrandingUploadErrorMessage(assetType, null);
  }

  private getPendingBrandingFile(assetType: BrandingAssetType): File | null {
    return assetType === 'logo' ? this.pendingLogoFile() : this.pendingFaviconFile();
  }

  private isBrandingMarkedForRemoval(assetType: BrandingAssetType): boolean {
    return assetType === 'logo' ? this.logoMarkedForRemoval() : this.faviconMarkedForRemoval();
  }

  private resetPendingBrandingChanges(): void {
    this.clearPendingBrandingSelection('logo');
    this.clearPendingBrandingSelection('favicon');
    this.logoMarkedForRemoval.set(false);
    this.faviconMarkedForRemoval.set(false);
    this.logoUploadStatusMessage.set(null);
    this.faviconUploadStatusMessage.set(null);
    this.logoUploadErrorMessage.set(null);
    this.faviconUploadErrorMessage.set(null);
  }

  private restorePendingBrandingStatusMessages(): void {
    this.setBrandingStatusMessage('logo', this.resolvePendingBrandingStatusMessage('logo'));
    this.setBrandingStatusMessage('favicon', this.resolvePendingBrandingStatusMessage('favicon'));
  }

  private resolvePendingBrandingStatusMessage(assetType: BrandingAssetType): string | null {
    if (this.getPendingBrandingFile(assetType)) {
      return this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.PENDING_UPLOAD_ON_SAVE');
    }

    if (this.isBrandingMarkedForRemoval(assetType)) {
      return this.translate.instant('SETTINGS_GENERAL.BRANDING_STATUS.PENDING_REMOVE_ON_SAVE');
    }

    return null;
  }

  private setBrandingStatusMessage(assetType: BrandingAssetType, message: string | null): void {
    if (assetType === 'logo') {
      this.logoUploadStatusMessage.set(message);
    } else {
      this.faviconUploadStatusMessage.set(message);
    }
  }

  private setBrandingUploadErrorMessage(
    assetType: BrandingAssetType,
    message: string | null
  ): void {
    if (assetType === 'logo') {
      this.logoUploadErrorMessage.set(message);
    } else {
      this.faviconUploadErrorMessage.set(message);
    }
  }

  private async getErrorDetail(error: unknown): Promise<string | null> {
    if (error instanceof ResponseError) {
      try {
        const parsed = await error.response.clone().json();
        if (parsed && typeof parsed === 'object') {
          const detail = (parsed as { detail?: unknown }).detail;
          if (typeof detail === 'string' && detail.trim().length > 0) {
            return detail;
          }
        }
      } catch {
        return null;
      }
    }

    if (!error || typeof error !== 'object') {
      return null;
    }

    const detail = (error as { error?: { detail?: unknown } }).error?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) {
      return detail;
    }

    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' && message.trim().length > 0 ? message : null;
  }

  private isNotFoundResponse(error: unknown): boolean {
    if (error instanceof ResponseError) {
      return error.response.status === 404;
    }

    if (!error || typeof error !== 'object') {
      return false;
    }

    const status = (error as { status?: unknown }).status;
    return status === 404;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  private async loadTimezoneOptions(): Promise<void> {
    const timezoneCatalog = await this.frontendConfigurationApi.getFrontendTimezoneCatalog();
    this.timezoneOptions = mapCatalogOptionsToSelectOptions(timezoneCatalog.items);
  }

  private async loadLanguageOptions(): Promise<void> {
    const languageCatalog = await this.frontendConfigurationApi.getFrontendLanguageCatalog();
    this.languageOptions = mapCatalogOptionsToSelectOptions(languageCatalog.items);
  }

  private ensureLanguageOption(language: string): void {
    if (!this.languageOptions.some((option) => option.value === language)) {
      this.languageOptions = [...this.languageOptions, { label: language, value: language }];
    }
  }

  private ensureTimezoneOption(timezone: string): void {
    if (!this.timezoneOptions.some((option) => option.value === timezone)) {
      this.timezoneOptions = [...this.timezoneOptions, { label: timezone, value: timezone }];
    }
  }
}

type LegacyPluginsByTarget = {
  web?: string[];
  mobile?: string[];
};

function pluginIds(plugins: string[] | LegacyPluginsByTarget | null | undefined): string[] {
  if (Array.isArray(plugins)) {
    return plugins;
  }
  return [...(plugins?.web ?? []), ...(plugins?.mobile ?? [])];
}
